import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { instance } from "../../../utils/utils";
import TrainingCard from "./trainingCard";
import {
  parse,
  addDays,
  format,
  startOfWeek,
  differenceInCalendarWeeks,
} from "date-fns";
import { es } from "date-fns/locale";
import TrainingFormModal from "./trainingFormModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./styles.css";

const TrainingPlanView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const membershipId = state?.membershipId;
  const userId = state?.userId;

  const [trainingPlanId, setTrainingPlanId] = useState(null);
  const [itemsGrouped, setItemsGrouped] = useState({});
  const [weekStart, setWeekStart] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const calculateWeekOffset = (selectedDate) => {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 0 });
    const startOfSelectedWeek = startOfWeek(new Date(selectedDate), {
      weekStartsOn: 0,
    });

    return differenceInCalendarWeeks(startOfSelectedWeek, startOfCurrentWeek);
  };

  useEffect(() => {
    if (!membershipId && !userId) return;

    const fetchTrainingPlan = async () => {
      try {
        const hasValidUserId =
          userId !== null && userId !== undefined && userId > 0;

        const params = hasValidUserId
          ? { member_id: userId }
          : { membership_id: membershipId };

        const res = await instance.get("memberships/new/training_plan", {
          params,
        });

        const planId = res.data?.training_plan?.id;
        if (planId) setTrainingPlanId(planId);
      } catch (error) {
        console.error("❌ Error al obtener plan de entrenamiento:", error);
      }
    };

    fetchTrainingPlan();
  }, [membershipId, userId]);

  useEffect(() => {
    if (!trainingPlanId) return;

    const fetchPublishedSessions = async () => {
      try {
        const res = await instance.get(
          "memberships/new/training_plan/published",
          {
            params: {
              training_plan_id: trainingPlanId,
              year,
              month,
              week_offset: weekOffset,
              search: search || undefined,
            },
          }
        );

        setItemsGrouped(res.data.items_grouped || {});
        setWeekStart(res.data.week_start);
      } catch (error) {
        console.error("❌ Error al obtener sesiones publicadas:", error);
      }
    };

    fetchPublishedSessions();
  }, [trainingPlanId, weekOffset, year, month, search, reloadTrigger]);

  const handlePrevWeek = () => setWeekOffset((prev) => prev - 1);
  const handleNextWeek = () => setWeekOffset((prev) => prev + 1);

  const baseDate = weekStart
    ? parse(weekStart, "dd-MM-yyyy", new Date())
    : new Date();

  return (
    <div className="training-plan-view p-4">
      <div className="training-plan-view-main gap-3 p-3">
        <div className="training-plan-view-top mb-5">
          <div className="training-plan-view-side left gap-2">
            <button
              className="training-plan-view-button-back"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <span className="training-plan-view-label-back">
              Perfil del Atleta
            </span>
          </div>

          <div className="training-plan-view-title fw-bold">
            {userId !== null && userId !== undefined && userId > 0 ? (
              <>Plan de entrenamiento individual</>
            ) : (
              <>Plan de entrenamiento general</>
            )}
          </div>

          <div className="training-plan-view-side right" />
        </div>

        <div className="training-plan-view-filters d-flex align-items-center justify-content-between gap-3 flex-wrap mt-3 mb-3">
          <div className="training-plan-view-search-container d-flex align-items-center px-3">
            <input
              type="text"
              placeholder="Búsqueda"
              className="training-plan-view-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="bi bi-search text-white ms-2"></i>
          </div>

          <div className="d-flex align-items-center gap-5 flex-wrap">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <span className="training-plan-view-filter-month fw-bold">
                  {format(baseDate, "LLLL", { locale: es })}
                </span>
                <span className="training-plan-view-filter-year fw-bold">
                  {format(baseDate, "yyyy")}
                </span>
                <div className="position-relative d-inline-block">
                  <i
                    className="bi bi-caret-down-fill text-primary cursor-pointer"
                    onClick={() => setShowCalendar((prev) => !prev)}
                  ></i>

                  {showCalendar && (
                    <div
                      className="calendar-dropdown position-absolute start-50 translate-middle-x mt-2"
                      style={{ zIndex: 100 }}
                    >
                      <DatePicker
                        selected={new Date()}
                        onChange={(date) => {
                          const newOffset = calculateWeekOffset(date);
                          setWeekOffset(newOffset);
                          setShowCalendar(false);
                        }}
                        inline
                        locale={es}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="training-plan-view-nav-arrow"
                  onClick={handlePrevWeek}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button
                  className="training-plan-view-nav-arrow"
                  onClick={handleNextWeek}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
            {trainingPlanId && (
              <button
                className="training-plan-view-add-button"
                onClick={() => {
                  setSelectedSessionId(null);
                  setShowFormModal(true);
                }}
              >
                Agregar Entrenamiento
              </button>
            )}
          </div>
        </div>

        <div className="training-plan-view-week">
          {Array.from({ length: 7 }).map((_, dayIndex) => {
            const date = addDays(baseDate, dayIndex);
            const dayName = format(date, "EEEE", { locale: es });
            const formattedDate = format(date, "dd 'de' MMMM", { locale: es });
            const formattedDateForComparison = format(date, "dd-MM-yyyy");

            const allSessions = Object.values(itemsGrouped).flat();
            const sessions = allSessions.filter(
              (session) => session.launch_date === formattedDateForComparison
            );

            return (
              <>
                <div
                  key={dayIndex}
                  className="training-plan-view-day-colunm gap-2 w-100 p-2"
                >
                  <div className="training-plan-view-day-title gap-2">
                    <span className="training-plan-view-day-label">
                      {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                    </span>
                    <span className="training-plan-view-day-date">
                      {formattedDate}
                    </span>
                  </div>

                  <div className="training-plan-view-day-cards gap-3">
                    {sessions.length === 0 ? (
                      <small className="training-plan-view-day-date">
                        Sin entrenamientos
                      </small>
                    ) : (
                      sessions.map((session) => (
                        <TrainingCard
                          key={session.id}
                          id={session.id}
                          date={session.launch_date}
                          type={session.class_name}
                          coach={session.teacher_name}
                          status={session.status}
                          variant="view"
                          trainingPlanId={trainingPlanId}
                          onRefresh={() => setReloadTrigger((prev) => prev + 1)}
                        />
                      ))
                    )}
                  </div>
                </div>
                {dayIndex < 6 && (
                  <div className="training-plan-view-colunm-line"></div>
                )}
              </>
            );
          })}
        </div>
      </div>
      <TrainingFormModal
        show={showFormModal}
        onClose={() => setShowFormModal(false)}
        sessionId={selectedSessionId}
        trainingPlanId={trainingPlanId}
        onRefresh={() => setReloadTrigger((prev) => prev + 1)}
      />
    </div>
  );
};

export default TrainingPlanView;
